import React, { useEffect, useState } from 'react';

import { SubmitHandler, useForm } from 'react-hook-form';

import {
  confirmPasswordResetCode,
  sendPasswordResetCode,
} from '../../services/auth';
import { isPasswordSecure } from '../../services/auth/utils';
import CollapsableNotification from '../CollapsableNotification';
import VerificationCode from '../VerificationCode';

import {
  FormContainer, RootContainer, StyledButton, StyledTextField,
} from './index.styles';

interface FormValues {
  email: string;
  password: string,
  confirmPassword: string,
}

interface NewPasswordFormProps {
  onComplete: (data: FormValues) => void;
  currentEmail?: string;
}

const NewPasswordForm = ({
  onComplete,
  currentEmail,
} : NewPasswordFormProps) => {
  const { register, handleSubmit, formState: { errors } } = useForm<FormValues>();
  const [errorOpen, setErrorOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (currentEmail) {
      // If the email is already known, register that into the form, such that
      // it doesn't need to be filled out
      register('email', { value: currentEmail });
    }
  }, []);

  const onSuccess = (data: FormValues) => {
    setLoading(false);
    onComplete(data);
  };

  const onFailure = (err: any) => {
    setLoading(false);
    setErrorMessage(err.message || 'An error has occurred');
    setErrorOpen(true);
  };

  const onSubmit: SubmitHandler<FormValues> = async (data: FormValues) => {
    setLoading(true);
    if (data?.password !== data?.confirmPassword) {
      onFailure({ message: 'Passwords must match' });
      return;
    }
    if (isPasswordSecure(data?.password)) {
      try {
        await sendPasswordResetCode(data.email);
        onSuccess(data);
      } catch (err) {
        onFailure(err);
      }
    } else {
      onFailure({ message: 'Password must be at least 8 character long with a letter and a number' });
    }
  };

  const getErrorMessage = (type: string | undefined, field: string): string => {
    switch (type) {
      case 'pattern':
        return `Please enter a valid ${field}`;
      case 'required':
        return `Please enter your ${field}`;
      default:
        return `Error: ${type}`;
    }
  };

  return (
    <FormContainer onSubmit={handleSubmit(onSubmit)}>
      <CollapsableNotification
        open={errorOpen}
        close={() => setErrorOpen(false)}
        message={errorMessage}
      />
      {!currentEmail && (
        <StyledTextField
          placeholder="Email"
          {...register('email', { required: true, pattern: /^\S+@\S+$/i })}
          error={!!errors.email}
          errorMessage={errors.email ? getErrorMessage(errors.email.type, 'email') : ''}
        />
      )}
      <StyledTextField
        placeholder="New Password"
        type="password"
        {...register('password', { required: true })}
        error={!!errors.password}
        errorMessage={errors.password ? getErrorMessage(errors.password.type, 'new password') : ''}
      />
      <StyledTextField
        placeholder="Confirm Password"
        type="password"
        {...register('confirmPassword', { required: true })}
        error={!!errors.password}
      />
      <StyledButton type="submit" loading={loading}>
        Reset Password
      </StyledButton>
    </FormContainer>
  );
};

interface PasswordResetCodeEntryProps {
  email: string,
  newPassword: string,
  onConfirm: () => void,
}

const PasswordResetCodeEntry = ({ email, newPassword, onConfirm }: PasswordResetCodeEntryProps) => (
  <VerificationCode
    source="email"
    confirm={async ({ verification }) =>
      confirmPasswordResetCode(email, verification, newPassword)}
    resend={async () => sendPasswordResetCode(email)}
    onConfirm={onConfirm}
  />
);

interface PasswordResetProps {
  onComplete: () => void;
  currentEmail?: string;
  resetFormPostComplete?: boolean;
}

const PasswordReset = ({ onComplete,
  currentEmail,
  resetFormPostComplete = false }: PasswordResetProps) => {
  const [isCodeSent, setIsCodeSent] = useState(false);
  const [userData, setUserData] = useState<FormValues>({
    email: '', password: '', confirmPassword: '',
  });

  const resetForm = () => {
    setUserData({
      email: '', password: '', confirmPassword: '',
    });
    setIsCodeSent(false);
  };

  return (
    <RootContainer>
      {(isCodeSent ? (
        <PasswordResetCodeEntry
          email={userData.email}
          newPassword={userData.password}
          onConfirm={() => {
            if (resetFormPostComplete) resetForm();
            onComplete();
          }}
        />
      ) : (
        <NewPasswordForm
          onComplete={(data: FormValues) => {
            setIsCodeSent(true);
            setUserData(data);
          }}
          currentEmail={currentEmail}
        />
      ))}
    </RootContainer>
  );
};

export default PasswordReset;
